import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';

import FollowedDescription from '@components/dataDisplay/board/FollowedDescription';
import useMouse from '@react-hook/mouse-position';
import DetailModal from './DetailModal';
import WalletPrice from '../component/WalletPrice';
import { IstoreItem } from '@typings/store/storeItem.interface';
import ItemImgCard from '@components/dataDisplay/Store/ItemImgCard';
import StoreStockBadge from '@components/dataDisplay/Store/StoreStockBadge';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  /* max-width: 120px; */
  height: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--lineColor);
  background-color: var(--cardBackgroundColor);

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;

  &:hover {
    border: 1px solid var(--primaryColor);
    cursor: var(--hoverCursorURL), pointer;
  }
`;

const TitleSec = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  line-height: 1.2;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  text-align: center;

  .description {
    color: var(--subTextColor);
    font-size: 12px;
    text-align: center;
    word-break: keep-all;
    line-height: 1.2;
  }
`;

const DiscountBedge = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  padding: 4px;
  isolation: isolate;

  color: var(--buttonTextColor);
  font-size: 13px;
  font-weight: 800;

  &::before {
    position: absolute;
    content: '';
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 42px 60px 0 0;
    border-color: var(--primaryColor) transparent transparent transparent;
    border-top-left-radius: 4px;
    transform: rotate(0deg);
    z-index: -1;
  }
`;

type Props = {
  data: IstoreItem;
  collectionPath: string;
};
function StoreItem({ data, collectionPath }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const targetRef = useMouse(ref);
  const [mouseOn, setMouseOn] = useState(false);
  const [detailShow, setDetailShow] = useState(false);
  const [currentItemID, setCurrentID] = useState(0);

  const handleMouseOver = useCallback((id) => {
    setMouseOn(true);
    setCurrentID(id);
  }, []);

  const closdDetailModal = useCallback(() => {
    setDetailShow(false);
  }, []);

  function handleClick(id) {
    setCurrentID(id);
    setDetailShow(true);
  }

  return (
    <Wrapper
      ref={ref}
      onClick={() => {
        setMouseOn(false);
        handleClick(data.id);
      }}
      onMouseEnter={() => {
        setMouseOn(true);
        handleMouseOver(data.id);
      }}
      onMouseLeave={() => setMouseOn(false)}
    >
      {data.eventOn && <DiscountBedge>{data.bedgeText}</DiscountBedge>}
      <ItemImgCard data={data} />
      <TitleSec>
        <div style={{ textAlign: 'center' }}>{data.title}</div>
        <span className="description">{data.description}</span>
        <StoreStockBadge data={data} />
      </TitleSec>
      <WalletPrice price={Number(data.price)} isAbsolute={false} />
      <FollowedDescription
        data={data}
        targetRef={targetRef}
        mouseOn={mouseOn && !detailShow}
      />
      <AnimatePresence>
        {detailShow && (
          <DetailModal data={data} closeModal={closdDetailModal} />
        )}
      </AnimatePresence>
    </Wrapper>
  );
}

export default StoreItem;
